import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';

import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { MDBModalRef } from 'ng-uikit-pro-standard';
import { NgxSpinnerService } from 'ngx-spinner';

import { BaryComponentHelper } from '../../../../util/bary-component-helper';

import { TransportIncidentService } from '../../../../../../services/transport-incident.service';
import { TransportIncidentItemService } from '../../../../../../services/transport-incident-item.service';

import { ITransportIncidentItem } from '../../../../../../models/transport-incident-item.model';
import { TransportIncident } from '../../../../../../models/transport-incident.model';
import { EstimationStatus, LastEstimate } from '../../../../../../models/estimate-result-bundle.model';

import { BaryModalEvent, IBaryModalEvent } from '../../../../../../models/bary-modal-event';

@Component({
  selector: 'jhi-bary-modal-add-transport-incident',
  templateUrl: './bary-modal-add-transport-incident.component.html',
  styleUrls: ['./bary-modal-add-transport-incident.component.scss'],
})
export class BaryModalAddTransportIncidentComponent implements OnInit {
  readonly #modalRef = inject(MDBModalRef);
  readonly #transportIncidentItemService = inject(TransportIncidentItemService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #transportIncidentService = inject(TransportIncidentService);

  @Input() public transportOrderId!: number;
  @Input() public transporterId!: number;

  public incidentForm!: FormGroup;

  public action = BaryComponentHelper.generateAction<IBaryModalEvent>();
  public incidentItemsOptions: { label: string; value: string }[] = [];
  public estimationInfo: LastEstimate = new LastEstimate({ status: EstimationStatus.QUOTATION_RESPONSE, estimation: { basePrice: 0 } });
  public priceIsUpdated = false;

  public ngOnInit(): void {
    this.#transportIncidentItemService
      .query({ 'transporterId.equals': this.transporterId })
      .pipe(map((res: HttpResponse<ITransportIncidentItem[]>) => res.body ?? []))
      .subscribe((items: ITransportIncidentItem[]) => {
        this.incidentItemsOptions = items.map(
          it =>
            ({ label: it.name, value: it.name } as {
              label: string;
              value: string;
            })
        );
        this.incidentItemsOptions.push({ label: 'Autre', value: 'Autre' });
        this.initForm();
        this.updateEstimate(0);
      });
  }

  private initForm(): void {
    this.incidentForm = new FormGroup({
      cause: new FormControl('', [Validators.required]),
      comment: new FormControl(''),
      hasGazoleTax: new FormControl(true),
      incidentAmount: new FormControl(0, [Validators.min(0.01)]),
      accept: new FormControl(null, [Validators.requiredTrue]),
    });

    this.incidentForm.controls['incidentAmount'].valueChanges.pipe(distinctUntilChanged(), debounceTime(500)).subscribe({
      next: (val): void => this.updateEstimate(val),
    });

    this.incidentForm.controls['hasGazoleTax'].valueChanges.subscribe({
      next: (val): void => this.updateEstimate(undefined, val),
    });
  }

  private updateEstimate(price?: number, hasGazoleTax?: boolean): void {
    const incident = {
      ...this.incidentForm.value,
      incidentAmount: price ?? this.incidentForm.controls['incidentAmount'].value,
      hasGazoleTax: hasGazoleTax ?? this.incidentForm.controls['hasGazoleTax'].value,
      originTransportOrder: this.transportOrderId,
    };
    this.priceIsUpdated = false;
    this.#transportIncidentService.estimate(incident).subscribe({
      next: (estimationInfo: LastEstimate): void => {
        estimationInfo.status = EstimationStatus.QUOTATION_RESPONSE;
        this.estimationInfo = estimationInfo;
        this.priceIsUpdated = true;
      },
    });
  }

  public getControl(controlName: string): FormControl {
    return this.incidentForm.get(controlName) as FormControl;
  }

  public submit(): void {
    if (this.incidentForm.invalid) {
      return;
    }

    const gazoleTaxAmount = this.getControl('hasGazoleTax').value ? this.estimationInfo.estimation.gazoleTaxAmount : undefined;
    const gazoleTaxRate = this.getControl('hasGazoleTax').value ? this.estimationInfo.estimation.gazoleTaxRate : undefined;
    const newIncident = new TransportIncident({
      ...this.incidentForm.value,
      gazoleTaxAmount,
      gazoleTaxRate,
      originTransportOrder: this.transportOrderId,
    });

    this.#spinnerService.show().then();
    this.#transportIncidentService
      .declare(newIncident)
      .subscribe({
        next: () => this.action.next(new BaryModalEvent({ submit: true })),
        error: (): void => this.action.next(new BaryModalEvent({ failure: true })),
      })
      .add(() => {
        this.#spinnerService.hide().then();
        this.#modalRef.hide();
        window.scrollTo(0, 0);
      });
  }

  public handleClick(from: any): void {
    this.action.next(new BaryModalEvent(from));
    this.#modalRef.hide();
  }
}
